<template>
  <v-row>
    <v-col cols="12" md="6" :lg="isByproductsContract ? 3 : 4">
      <DatePicker
      @date-picked="setStartDate"
      elementId="contract-effective-date"
      :startDate="contractInfo.effectiveDate"
      @valid-date="(val) => {
        isValidStartDate = val
      }"
      v-if="showStartDate"
      dateLabel="Effective"
      ></DatePicker>
    </v-col>
    <v-col cols="12" md="6" :lg="isByproductsContract ? 3 : 4">
      <DatePicker
      @date-picked="endDatePicked"
      elementId="contract-expiration-date"
      :startDate="contractInfo.expirationDate"
      @valid-date="(val) => {
        isValidEndDate = val
      }"
      dateLabel="Expiration"
      v-if="showEndDate"
      ></DatePicker>
    </v-col>
    <v-col cols="12" :lg="isByproductsContract ? 3 : 4">
      <v-text-field
        id="contract-distance"
        v-model="contractInfo.distance"
        :label="$t('distance')"
        color="black"
        :rules="[rules.positiveIntegerOrBlank, rules.validDistance]"
      ></v-text-field>
    </v-col>
    <v-col v-if="isByproductsContract" cols="12" :lg="isByproductsContract ? 3 : 4">
      <v-text-field
        v-if="!isByproductPurchase && !isByproductHauling"
        v-model="contractInfo.tareWeight"
        id="contract-tare-weight"
        color="black"
        :label="$t('tareWeightLBS')"
        :rules="[rules.positiveIntegerOrBlank, rules.validWeightPounds]"
      ></v-text-field>
    </v-col>
    <v-col cols="12" sm="4" lg="auto">
      <v-checkbox
        v-if="showRequiresExt1"
        id="requires-ext-ticket-1"
        v-model="contractInfo.requiresExt1"
        color="primary"
        :label="$t('requiresExt1')"
      />
      <v-checkbox
        v-if="showRequiresExt2"
        id="requires-ext-ticket-2"
        v-model="contractInfo.requiresExt2"
        color="primary"
        :label="$t('requiresExt2')"
        hide-details
        @change="storeRequiresExt2Selection()"
      >
        <template #append v-if="showTicketBooks">
          <Icon
            iconColor="primary"
            :disabled="!canSelectTicketBooks"
            icon="mdi-book-open-variant"
            :small="false"
            :tooltipText="$t('specifyTicketBooks')"
            @icon-clicked="ticketBookDialog = true"/>
        </template>
      </v-checkbox>
      <span v-if="showTicketBooks && contractInfo.ticketBookIds.length > 0" class="text--secondary">
        {{ ticketBookSubtitleString }}
      </span>
      <v-dialog v-model="ticketBookDialog" max-width="400px" v-if="ticketBookDialog">
        <TicketBookSelector
          :accountIds="[destinationAccountId]"
          :propTicketBookIds="contractInfo.ticketBookIds"
          :propTicketBooksAccountIds="[destinationAccountId]"
          :contractId="contractId"
          fixedAccount
          :headerColorClass="contractColor"
          @close="ticketBookDialog = false"
          @ticket-books-selected="ticketBooksSelected"/>
      </v-dialog>
    </v-col>
    <v-col cols="12" sm="4" md="3" lg="auto">
      <v-checkbox
        v-if="!hideDraft"
        id="toggle-draft"
        v-model="contractInfo.isDraft"
        color="primary"
        :label="$t('markAsDraft')"
        :disabled="lockAsDraft"
      />
      <v-checkbox
        id="on-pause"
        v-model="contractInfo.paused"
        color="primary"
        :label="$t('markOnPause')"
      />
    </v-col>
    <v-col v-if="isLogsContract" cols="12" sm="4" lg="auto">
      <v-checkbox
        id="requires-logger"
        v-model="contractInfo.requiresLogger"
        color="primary"
        :label="$t('requiresLogger')"
      />
    </v-col>
    <v-col cols="12" md="3" lg="auto" xl="auto" v-if="showAllowPickupLoads">
      <v-checkbox
      id="allow-pickup"
      v-model="contractInfo.allowPickupLoads"
      color="primary"
      :label="$t('allowLoadPickup')"
      @change="storeAllowPickupLoadsSelection()"/>
    </v-col>
  </v-row>
</template>

<script>
import fieldRules from '@/utils/rules.js'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import { ContractType, ContractMode } from '@/utils/Enumerations.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { colorClassForContractMode } from '@/utils/componentHelpers'

export default {
  name: 'ContractInfo',
  components: {
    DatePicker: () => import('../helper/DatePicker.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    TicketBookSelector: () => import('@/components/tract/tract-form/TicketBookSelector.vue')
  },

  props: {
    propContractInfo: {
      type: Object,
      required: true
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    },
    contractMode: {
      type: Object,
      required: true
    },
    tract: {
      type: Object,
      default: undefined
    },
    lockAsDraft: {
      type: Boolean,
      default: false
    },
    hideDraft: {
      type: Boolean,
      default: false
    },
    contractType: {
      type: Object,
      required: false
    },
    destinationAccountId: {
      type: Number,
      default: undefined
    },
    contractId: {
      type: Number,
      default: undefined
    },
    hasVolumeActivity: {
      type: Boolean,
      default: false
    }
  },

  created () {
    this.contractInfo = cloneDeep(this.propContractInfo)
    this.initialTicketBookIds = [...this.contractInfo.ticketBookIds]
    if (!this.editing) {
      this.contractInfo.effectiveDate = moment().format()
      this.contractInfo.expirationDate = moment().add(90, 'days').format()

      if (this.isByproductsContract && !this.isByproductPurchase && !this.isByproductHauling) {
        this.contractInfo.allowPickupLoads = localStorage.getItem(LocalStorageKeys.BP_CONTRACT_PICK_UP_LOADS) === 'true'
      }

      if (this.showRequiresExt2) {
        this.contractInfo.requiresExt2 = localStorage.getItem(LocalStorageKeys.CONTRACT_REQUIRES_EXT2) === 'true'
      }
    }
  },

  data: () => ({
    ContractMode,
    contractInfo: {},
    isValidStartDate: true,
    isValidEndDate: true,
    showStartDate: true,
    showEndDate: true,
    ticketBookDialog: false,
    initialTicketBookIds: [],
    rules: fieldRules.rules
  }),

  watch: {
    contractInfo: {
      handler (val) {
        this.$emit('contract-info-changed', val)
        this.isButtonDisabled()
      },
      deep: true
    },

    isValidStartDate () {
      this.isButtonDisabled()
    },
    isValidEndDate () {
      this.isButtonDisabled()
    },

    propStartDate (val) {
      if (val && !this.editing) {
        this.contractInfo.effectiveDate = val
        this.showStartDate = false
        this.$nextTick(_ => {
          this.showStartDate = true
        })
      }
    },

    propEndDate (val) {
      if (val && !this.editing) {
        this.contractInfo.expirationDate = val
        this.showEndDate = false
        this.$nextTick(_ => {
          this.showEndDate = true
        })
      }
    },

    isByproductPurchase (val) {
      if (!val) return
      this.contractInfo.allowPickupLoads = false
      this.contractInfo.tareWeight = 0
    },

    isByproductHauling (val) {
      if (!val) return
      this.contractInfo.requiresExt1 = false
      this.contractInfo.allowPickupLoads = false
      this.contractInfo.tareWeight = 0
    },

    'contractInfo.requiresExt2' (val) {
      if (!val) {
        this.contractInfo.ticketBookIds = []
      } else if (this.initialTicketBookIds.length > 0) {
        this.contractInfo.ticketBookIds = [...this.initialTicketBookIds]
      }
    },

    destinationAccountId (val, oldVal) {
      if (oldVal == null) return
      this.initialTicketBookIds = []
      this.contractInfo.ticketBookIds = []
    }
  },

  computed: {
    propStartDate () {
      return this.tract?.harvestStartDate
    },

    propEndDate () {
      return this.tract?.harvestEndDate
    },

    isLogsContract () {
      return this.contractMode.value === ContractMode.Logs.value
    },

    isByproductsContract () {
      return this.contractMode.value === ContractMode.Byproducts.value
    },

    isLogYardSaleContract () {
      return this.contractMode.value === ContractMode.LogYardSale.value
    },

    showAllowPickupLoads () {
      if (this.isByproductsContract) return !this.isByproductPurchase && !this.isByproductHauling
      return this.isLogYardSaleContract && this.editing && (this.hasVolumeActivity || this.contractInfo.allowPickupLoads === true)
    },

    isByproductPurchase () {
      return this.contractType?.value === ContractType.ByproductPurchase.value
    },

    isByproductHauling () {
      return this.contractType?.value === ContractType.ByproductHauling.value
    },

    showRequiresExt1 () {
      return this.contractMode.value !== ContractMode.Transfers.value && !this.isByproductHauling
    },

    showRequiresExt2 () {
      return this.contractMode.value !== ContractMode.Transfers.value
    },

    showTicketBooks () {
      return this.contractMode.value === ContractMode.LogYardSale.value ||
        this.contractMode.value === ContractMode.Byproducts.value
    },

    canSelectTicketBooks () {
      return this.contractInfo.requiresExt2 === true && this.destinationAccountId != null
    },

    contractColor () {
      return colorClassForContractMode(this.contractMode.value)
    },

    ticketBookSubtitleString () {
      return this.$tc('ticketBookCount', this.contractInfo.ticketBookIds.length)
    }
  },

  methods: {
    endDatePicked (val) {
      this.contractInfo.expirationDate = val
    },

    setStartDate (val) {
      this.contractInfo.effectiveDate = val
    },

    isButtonDisabled () {
      const tempEndDate = new Date(this.contractInfo.endDate).setHours(0, 0, 0, 0)
      const tempStartDate = new Date(this.contractInfo.startDate).setHours(0, 0, 0, 0)
      this.$emit('is-button-disabled', !(this.isValidStartDate && this.isValidEndDate) || (tempStartDate > tempEndDate))
      if (tempStartDate > tempEndDate) {
        this.setSnackError(this.$t('endDateBeforeStartDate'))
      }
    },

    storeAllowPickupLoadsSelection () {
      if (this.isByproductsContract) {
        localStorage.setItem(LocalStorageKeys.BP_CONTRACT_PICK_UP_LOADS, this.contractInfo.allowPickupLoads)
      }
    },

    storeRequiresExt2Selection () {
      localStorage.setItem(LocalStorageKeys.CONTRACT_REQUIRES_EXT2, this.contractInfo.requiresExt2)
    },

    ticketBooksSelected ({ selectedTicketBookIds }) {
      this.contractInfo.ticketBookIds = [...selectedTicketBookIds]
      this.initialTicketBookIds = [...selectedTicketBookIds]
      this.ticketBookDialog = false
    }
  }
}
</script>
